import { setStandaloneAssistant } from '@components/Assistant/assistantBridge'
import { pageContextFromQuery } from '@components/Assistant/assistantPageContext'
import AssistantPanel from '@components/Assistant/AssistantPanel'
import { KW } from '@components/Assistant/ui'
import appStore from '@stores/appStore'
import { ASSISTANT_UNAVAILABLE_MESSAGES } from '@stores/assistantAccess'
import assistantStore from '@stores/assistantStore'
import { observer } from 'mobx-react'
import { useEffect } from 'react'

/**
 * The Assistant on its own page (/view/assistant), opened in a separate browser tab with
 * "Open in new window". Links clicked here navigate the main Kawaconn tab (assistantBridge).
 * `?conversationId=` continues the conversation that was open in the drawer;
 * `?contextType=&contextId=` is the record that was on screen there — a snapshot,
 * shown as the Context chip and removable; it does not follow the main tab afterwards;
 * `?guide=<page>&section=<id>` opens the user guide at that page and section.
 */
const AssistantPage = () => {
  const unavailableReason = appStore.assistantUnavailableReason
  const enabled = !unavailableReason

  useEffect(() => {
    setStandaloneAssistant(true)
    const previousTitle = document.title
    document.title = 'AI Assistant · Kawaconn'
    return () => {
      setStandaloneAssistant(false)
      document.title = previousTitle
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      return
    }
    // Before anything can be asked: this page has no record of its own on screen.
    assistantStore.enterStandalone(pageContextFromQuery(window.location.search))
    assistantStore.openDrawer()
    const params = new URLSearchParams(window.location.search)
    const conversationId = params.get('conversationId')
    if (conversationId) {
      assistantStore.selectConversation(conversationId)
    }
    // Shared guide links: ?guide=<page>&section=<heading id>
    const guide = params.get('guide')
    if (guide) {
      assistantStore.openGuide(guide, params.get('section') ?? undefined)
    }
  }, [enabled])

  if (unavailableReason) {
    return (
      <div
        className="flex h-screen items-center justify-center p-6 text-sm"
        style={{ color: KW.muted }}
      >
        {ASSISTANT_UNAVAILABLE_MESSAGES[unavailableReason]}
      </div>
    )
  }

  return (
    <div
      className="flex h-screen justify-center"
      style={{ background: KW.soft2 }}
    >
      <main
        className="flex h-full w-full max-w-[920px] flex-col bg-white"
        style={{
          borderLeft: `1px solid ${KW.line}`,
          borderRight: `1px solid ${KW.line}`,
        }}
      >
        <AssistantPanel standalone />
      </main>
    </div>
  )
}

export default observer(AssistantPage)
